import { Boundaries } from "@planzone/core";
import { id, Identifiable, list, project, Task, task } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { calculateAdvancement } from "../../utils/calculateAdvancement";
import { resetState } from "../../utils/resetState";
import { reduceTask } from "../../common/task";
import { reduceList } from "../../common/list";
import { reduceProject } from "../../common/project";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { retrieveTaskError } from "@planzone/dto";

const initialState: State = [];

function handleRequest(
  state: State,
  taskId: Boundaries["retrieveTask"]["request"],
): State {
  const stateIndex = state.findIndex(({ id }) => id === taskId);

  return stateIndex !== -1
    ? [
        ...state.slice(0, stateIndex),
        {
          id: taskId,
          request: setRequestStateToPending(),
          task: {
            uuid: id,
            ...task,
            list: { uuid: id, ...list },
            project,
          },
          error: retrieveTaskError,
        },
        ...state.slice(stateIndex + 1),
      ]
    : [
        ...state,
        {
          id: taskId,
          request: setRequestStateToPending(),
          task: {
            uuid: id,
            ...task,
            list: { uuid: id, ...list },
            project,
          },
          error: retrieveTaskError,
        },
      ];
}

function handleSuccess(
  state: State,
  task: Boundaries["retrieveTask"]["success"],
): State {
  return state.map((item) =>
    item.id === task.uuid
      ? {
          ...item,
          task: { ...task, ...calculateAdvancement(task) },
          request: setRequestStateToSuccess(),
        }
      : item,
  );
}

function handleFailure(
  state: State,
  { taskId, error }: Boundaries["retrieveTask"]["failure"],
): State {
  return state.map((item) =>
    item.id === taskId
      ? { ...item, request: setRequestStateToFailure(), error }
      : item,
  );
}

function handleMoveListElementRequest(
  state: State,
  {
    nextParentList,
    projectElementId,
  }: Boundaries["moveListElement"]["request"],
): State {
  return state.map((item) =>
    item.id === projectElementId && nextParentList.uuid !== item.task.list.uuid
      ? { ...item, task: { ...item.task, list: nextParentList } }
      : item,
  );
}

function handleMoveListElementSuccess(
  state: State,
  {
    nextParentList,
    projectElementId,
  }: Boundaries["moveListElement"]["success"],
): State {
  return state.map((item) =>
    item.id === projectElementId && nextParentList.uuid !== item.task.list.uuid
      ? { ...item, task: { ...item.task, list: nextParentList } }
      : item,
  );
}

function handleMoveListElementFailure(
  state: State,
  {
    nextParentList,
    projectElementId,
    currentParentList,
  }: Boundaries["moveListElement"]["failure"],
): State {
  return state.map((item) =>
    item.id === projectElementId && nextParentList.uuid !== item.task.list.uuid
      ? { ...item, task: { ...item.task, list: currentParentList } }
      : item,
  );
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((item) => ({
    ...item,
    task: {
      ...item.task,
      ...reduceTask(item.task, event),
      list: reduceList(item.task.list, event),
      project: reduceProject(item.task.project, event),
    },
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveTaskRequested]: handleRequest,
      [events.retrieveTaskSucceeded]: handleSuccess,
      [events.retrieveTaskFailed]: handleFailure,
      [events.moveListElementRequested]: handleMoveListElementRequest,
      [events.moveListElementSucceeded]: handleMoveListElementSuccess,
      [events.moveListElementFailed]: handleMoveListElementFailure,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
